import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowRight } from 'lucide-react';
import { AuthLayout, AuthInput, AuthButton } from './AuthComponents';
import { useAuth } from '@/context/AuthContext';
import { RESET_PASSWORD } from '@/graphql/mutations';
import { ErrorBanner } from '@/components/ui/ErrorBanner';

export function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login } = useAuth();
    const { t } = useTranslation('auth');

    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');

    const [resetPassword, { loading }] = useMutation(RESET_PASSWORD);

    if (!token) {
        navigate('/forgot-password', { replace: true });
        return null;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!newPassword || !confirmPassword) {
            setError(t('resetPassword.error.fillAll'));
            return;
        }

        if (newPassword.length < 8) {
            setError(t('resetPassword.error.passwordLength'));
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(t('resetPassword.error.passwordMismatch'));
            return;
        }

        try {
            const { data } = await resetPassword({
                variables: { input: { token, newPassword } },
            });

            if (data?.resetPassword?.token) {
                await login(data.resetPassword.token);
                navigate('/');
            }
        } catch {
            setError(t('resetPassword.error.invalidToken'));
        }
    };

    return (
        <AuthLayout
            title={t('resetPassword.title')}
            subtitle={t('resetPassword.subtitle')}
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                <AuthInput
                    label={t('resetPassword.password.label')}
                    icon={Lock}
                    type="password"
                    placeholder={t('resetPassword.password.placeholder')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                />

                <AuthInput
                    label={t('resetPassword.confirmPassword.label')}
                    icon={Lock}
                    type="password"
                    placeholder={t('resetPassword.confirmPassword.placeholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />

                {error && <ErrorBanner message={error} className="animate-in shake" />}

                <AuthButton type="submit" isLoading={loading}>
                    {t('resetPassword.submit')} <ArrowRight size={20} />
                </AuthButton>
            </form>
        </AuthLayout>
    );
}
